import React, { useEffect, useState } from 'react';
import { Col, FormGroup, Input, Label } from 'reactstrap';

export interface DropdownProps {
    options: { value: string | number; label: string }[];
    value?: string | number | string[];
    title: string;
    multiple?: boolean;
    onOptionSelected?: (selectedOption: string | number | string[]) => void;
    onOptionTextSelected?: (selectedOption: string | number | string[]) => void;
    placeholder?: string;
}

export default function SelectDropdown({
    options,
    title,
    multiple = false,
    value,
    onOptionSelected,
    onOptionTextSelected,
    placeholder
}: DropdownProps) {
    const [selectedOption, setSelectedOption] = useState<string | number | string[] | undefined>(value);

    // If there is only one option, preselect it
    useEffect(() => {
        if ((!value || (Array.isArray(value) && value.length === 0)) && options.length === 1) {
            setSelectedOption(options[0].value);
            onOptionSelected?.(options[0].value);
            onOptionTextSelected?.([options[0].label]);
        } else {
            setSelectedOption(value);
        }
        // Only run effect on options or value change
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [options, value]);

    const handleOptionChange = (event: React.ChangeEvent<any>) => {
        const selectElement = event.target as HTMLSelectElement;

        const selectedValue = multiple
            ? Array.from(selectElement.selectedOptions, (option) => option.value)
            : selectElement.value;

        const selectedText = options
            .filter(option =>
                multiple
                    ? (selectedValue as string[]).includes(option.value.toString())
                    : option.value.toString() === selectedValue.toString()
            )
            .map(option => option.label);

        setSelectedOption(selectedValue);
        onOptionSelected?.(selectedValue);
        onOptionTextSelected?.(selectedText);
    };

    const inputValue = multiple
        ? (selectedOption as string[]) ?? []
        : (selectedOption as string | number) ?? '';

    return (
        <Col>
            <FormGroup>
                <Label>{title}</Label>
                <Input
                    type="select"
                    multiple={multiple}
                    value={inputValue}
                    onChange={handleOptionChange as React.ChangeEventHandler<HTMLInputElement>}
                >
                    {!multiple && <option value="">Select {placeholder || title}</option>}
                    {options.map((option, index) => (
                        <option value={option.value} key={index}>
                            {option.label}
                        </option>
                    ))}
                </Input>
            </FormGroup>
        </Col>
    );
}
